/**
 * sessionCheckoutClient.ts
 *
 * Typed fetch wrapper for POST /api/bookings/session-checkout.
 * Called from SessionBookingForm when the customer confirms their slot +
 * party selection and submits contact info.
 *
 * The route is server-only — this wrapper is the only surface the browser
 * touches. It never carries a price; the server builds the snapshot from the
 * product's party-pricing ladder + the validated addon keys.
 */

import type { SessionSnapshot, BookingTax } from '@sifly/shared-utils'
import type { PostCheckoutFlow } from '@sifly/booking-flows/schema'

export type SessionCheckoutResult = {
  clientSecret: string
  bookingId: string
  paymentIntentId: string
  expiresAt: string
  stripeAccountId: string
  publishableKey: string
  price: SessionSnapshot
  /** Canonical occurrence instant the hold landed on. */
  startsAt: string
  /** The flow definition PINNED to this booking (bookings.flow_version_id).
   *  Rendered as-is by the in-page runner so the pin stays the only source
   *  of truth — the client never re-resolves from booking_products.flow. */
  flow: PostCheckoutFlow
  /** What the server is about to charge — price + tax. Shown above the
   *  Payment Element so the customer sees the real number before paying,
   *  even when tax could not be pre-computed on an earlier step. */
  amountCents: number
  tax: BookingTax
}

export type StartSessionCheckoutInput = {
  productId: string
  /** The slot's startsAt from session-availability, echoed verbatim. */
  startsAt: string
  riders: number
  addonKeys: string[]
  contact: {
    email: string
    firstName: string
    lastName: string
    phone?: string
  }
  /** If the customer is changing slots on an existing hold, supply the
   *  prior bookingId here so the server cancels it before re-holding. */
  releaseBookingId?: string
}

export async function startSessionCheckout(
  input: StartSessionCheckoutInput,
): Promise<
  | { ok: true; data: SessionCheckoutResult }
  | { ok: false; status: number; error: string; reason?: string }
> {
  const res = await fetch('/api/bookings/session-checkout', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })

  const json = await res.json().catch(() => ({})) as Record<string, unknown>

  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      error: (json.error as string) ?? 'Checkout failed',
      reason: json.reason as string | undefined,
    }
  }

  return { ok: true, data: json as SessionCheckoutResult }
}
